import { useEffect } from 'react';
import styles from './index.module.scss';
import {
  getActivityLogs,
  getRcaQuestionTree,
  getSingleAnomalyTicket
} from '../../actions/AnomalyTrackerAction';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import { Tag, Typography } from '@navi/web-ui/lib/primitives';
import SuspenseLoader from '@cp/src/components/SkeletonLoader/SuspenseLoader';
import LineLoader from '@cp/src/components/SkeletonLoader/LineLoader';
import AnomalyView from './AnomalyView';
import { readQueryParams } from '@cp/src/utils/QueryParamsHelper';
import { AnomalyPriorityColor, AnomalyStatus, AnomalyTableKeys } from '../../types';
import { getAssignedToName } from '../../utils';
import { useParams } from 'react-router-dom';
import cx from 'classnames';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';

const AnomalyDetails = () => {
  const { tabId = '' } = useParams();
  const params = readQueryParams();
  const selectedAnomalyId = params?.[AnomalyTableKeys.ANOMALY_TABLE_ACTION]?.selectedId;
  const dispatch = useDispatch();
  const isLoading = useSelector(
    (state: RootState) => state?.anomalyTracker?.anomalyDetails?.loading
  );
  const anomalyDetails = useSelector(
    (state: RootState) => state?.anomalyTracker?.anomalyDetails?.data
  );

  const userReferenceId = useSelector((state: RootState) => state?.common?.userData?.referenceId);

  const { assignedTo, priority } = anomalyDetails || {};
  const { name, id: assignedToRefId } = assignedTo || {};

  const isClosedAnomalyView = tabId === AnomalyStatus.CLOSED;

  useEffect(() => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_ANOMALY_TRACKER_SINGLE_ANOMALY_LOADED, {
      anomalyId: selectedAnomalyId,
      anomalyStatus: isClosedAnomalyView ? AnomalyStatus.CLOSED : AnomalyStatus.OPEN
    });
    dispatch(getSingleAnomalyTicket(selectedAnomalyId));
    dispatch(getActivityLogs(selectedAnomalyId));
    if (!isClosedAnomalyView) {
      dispatch(getRcaQuestionTree(selectedAnomalyId));
    }
  }, []);

  const isAssignedToYou = assignedToRefId === userReferenceId;

  return (
    <div className="w-[984px] h-[608px]">
      <div className={cx(styles.assignedToDetails, { [styles.closedTicket]: isClosedAnomalyView })}>
        <SuspenseLoader
          loading={isLoading}
          fallBack={
            <LineLoader numberOfLines={1} childrenType="P3" dimenshionUnit="px" lineWidth={100} />
          }
        >
          <Typography as="span" variant="p3" color="var(--grayscale-3)">
            {isClosedAnomalyView ? 'Last assigned to: ' : 'Assigned to: '}
            <Typography as="span" variant="p3" color="var(--grayscale-1)" className="font-medium">
              {getAssignedToName(name, isAssignedToYou)}
            </Typography>
          </Typography>
        </SuspenseLoader>
        <SuspenseLoader
          loading={isLoading}
          fallBack={
            <LineLoader numberOfLines={1} childrenType="P3" dimenshionUnit="px" lineWidth={100} />
          }
        >
          <Typography variant="p3" color="var(--grayscale-2)" className="flex">
            Priority:{' '}
            {priority ? (
              <Tag
                containerClass="ml-1 h-5"
                size="sm"
                color={AnomalyPriorityColor[priority as keyof typeof AnomalyPriorityColor]}
                variant="transparent"
                label={priority}
              />
            ) : (
              '-'
            )}
          </Typography>
        </SuspenseLoader>
      </div>
      {/* 37px is the height of the assignedToDetails  */}
      <div className="h-[calc(100%-37px)]">
        <AnomalyView />
      </div>
    </div>
  );
};

export default AnomalyDetails;
